import React, { useState, useEffect, Fragment } from 'react';
import ProjectTypeSelect from "./Type";
import ProjectDetails from "./Details";
import ProjectAddress from "./Address";
import ProjectImageUpload from "./ImageUpload"
import MaterialChoose from "../Material/Choose"
import { Alert, Button, Label, Input, Spinner, Toast, ToastBody, ToastHeader } from "reactstrap";
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { useLocation, useNavigate } from "react-router-dom";
import { InteractionRequiredAuthError, InteractionStatus } from '@azure/msal-browser';
import useSolution from '../../hooks/useSolution';

function ProjectCreate() {
    const [formData, setFormData] = useState({});
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [selectedProjectType, setSelectedProjectType] = useState();
    const [projectCreateKey, setProjectCreateKey] = useState(Date.now());
    const [toastMessageVisible, setToastMessageVisible] = useState(false);
    const [projectDetails, setProjectDetails] = useState();
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [apiToken, setApiToken] = useState();
    const isAuthenticated = useIsAuthenticated();
    const { instance, inProgress, accounts } = useMsal();
    const navigate = useNavigate();
    const location = useLocation();
    const solution = useSolution();

    useEffect(() => {
        if (inProgress === InteractionStatus.None) {
            if (!isAuthenticated) {
                localStorage.setItem('post-login-url', location.pathname);
                navigate('/auth/login');
            } else {
                const tokenRequest = {
                    scopes: ['https://BuildEB2C.onmicrosoft.com/buildeapidev/Builde.read'],
                    account: accounts[0]
                };

                instance.acquireTokenSilent(tokenRequest).then((tokenData) => {
                    setApiToken(tokenData.accessToken);
                }).catch(async (error) => {
                    if (error instanceof InteractionRequiredAuthError) {
                        return instance.acquireTokenPopup(tokenRequest).then((tokenData) => {
                            setApiToken(tokenData.accessToken);
                        });
                    }
                
                    alert("We couldn't get an authentication token.");
                });
            }
        }
     }, [isAuthenticated, navigate, location, inProgress, instance, accounts]);

     useEffect(() => {
        const created = solution.solution !== null && !solution.error;
        setToastMessageVisible(created);
        if (created) {
            clearForm();
        }
     }, [solution.solution, solution.error]);

    const handleProjectMaterialChange = (materialChange) => {        
        let newMaterials = [];

        setProjectMaterials((materials) => {
            if (materialChange.quantity <= 0) {
                newMaterials = materials.filter(material =>
                    material.id !== materialChange.id
                );
            } else {
                let materialState = materials.find(material =>
                    material.id === materialChange.id
                );

                if (materialState !== undefined) {
                    newMaterials = materials.map(material => {
                        if (material.id !== materialChange.id) {
                            return material;
                        } else {
                            return {
                                ...material,
                                quantity: materialChange.quantity,
                                price: materialChange.price
                            }
                        }
                    });
                } else {
                    newMaterials = [
                        ...materials,
                        materialChange
                    ];
                }
            }

            setTotalMaterialBudget(calculateMaterialBudget(newMaterials));
            return newMaterials;
        });
    }

    const [materialsChoose, setMaterialsChoose] = useState([<MaterialChoose key={0} />]);
    const [projectMaterials, setProjectMaterials] = useState([]);
    const [totalMaterialBudget, setTotalMaterialBudget] = useState(0);
    const [totalLaborBudget, setTotalLaborBudget] = useState(0);

    const openAlert = (message) => {
        setAlertMessage(message);
        setAlertOpen(true);
    }

    const handleProjectTypeChange = (projectType) => {
        setSelectedProjectType(projectType);
        setMaterialsChoose([
            <MaterialChoose
                key={0}
                isRemovable={false}
                onProjectMaterialChange={handleProjectMaterialChange}
                projectMaterials={projectMaterials}
                projectTypeId={projectType.id}
                apiToken={apiToken}
            />
        ]);
    }

    const handleAddMaterialChoose = (e) => {
        e.preventDefault();
        console.log(projectMaterials);
        setMaterialsChoose([...materialsChoose,
            <MaterialChoose
                key={materialsChoose.length}
                isRemovable="true"
                onProjectMaterialChange={handleProjectMaterialChange}
                projectMaterials={projectMaterials}
                projectTypeId={selectedProjectType ? selectedProjectType.id : null}
                apiToken={apiToken}
            />
        ]);
    }

    const handleTotalLaborBudgetChange = (e) => {
        setTotalLaborBudget(e.target.value);
    }

    const calculateMaterialBudget = (projectMaterials) => {
        let totalMaterialBudget = 0;
        projectMaterials.forEach((projectMaterial) => {
            if (projectMaterial.price && projectMaterial.quantity) {
                totalMaterialBudget += (projectMaterial.price * projectMaterial.quantity);
            }
        });
        return totalMaterialBudget;
    }

    const submitProject = () => {
        solution.create(getSolutionData());
    }

    const getSolutionData = () => {
        formData.createProjects = [getProjectData()];
        formData.totalBudget = parseFloat(totalMaterialBudget) + parseFloat(totalLaborBudget);
        //TODO: Set currency using service
        formData.currencyId = 1;

        return formData;
    }

    const getProjectData = () => {
        return {
            description: projectDetails,
            projectTypeId: selectedProjectType.id,
            currencyId: 1, //TODO: Set currency using service
            laborBudget: totalLaborBudget,
            createProjectMaterialDetails: getProjectMaterialDetails(),
            createProjectMediaFiles: uploadedFiles,
            calculateMaterialBudget: false
        }
    }

    const getProjectMaterialDetails = () => {
        let materialDetails = [];

        projectMaterials.forEach((projectMaterial) => {
            if (projectMaterial.price && projectMaterial.quantity) {
                materialDetails.push({
                    materialId: projectMaterial.material.id,
                    materialCategoryId: projectMaterial.material.materialCategory.id,
                    quantity: projectMaterial.quantity,
                    price: projectMaterial.price
                });
            }
        });

        return materialDetails;
    }

    const validateProject = () => {
        return !!formData.countryId
            && !!formData.administrativeAreaId
            && !!formData.locality
            && !!formData.addressLine
            && !!formData.postalCode
            && projectMaterials.length > 0
            && !isNaN(totalLaborBudget) && totalLaborBudget > 0;
    }

    const clearForm = () => {
        setProjectCreateKey(Date.now());
        setFormData({});
        setTotalLaborBudget(0);
        setTotalMaterialBudget(0);
    }

    return (
        <Fragment key={projectCreateKey}>
        <div className={ !isAuthenticated ? 'visibility-hidden' : ''}>
            <Toast className="position-fixed top-0 end-0 me-3 mt-3 bg-success" isOpen={ toastMessageVisible }>
                    <ToastHeader toggle={() => {setToastMessageVisible(!toastMessageVisible)}}>
                        Project created
                    </ToastHeader>
                    <ToastBody>
                        <span className="text-white">
                            You successfuly created the project {
                                solution.solution && solution.solution.id ?
                                    <a href={'solution/' + solution.solution.id}>#{solution.solution.id}</a> : ''
                            } !
                        </span>
                    </ToastBody>
            </Toast>
            <div>
                <Alert color="danger" isOpen={ alertOpen } toggle={() => { setAlertOpen(!alertOpen) }}>
                    <p className="mb-0">
                        { alertMessage }
                    </p>
                    <hr />
                </Alert>
            </div>
            <div>
                <h3>Create Project</h3>
                <p>Select the project type</p>
            </div>
            <div>
                <ProjectTypeSelect onError={ openAlert } onProjectTypeChange={ handleProjectTypeChange } />
            </div>
            <div>
                { selectedProjectType !== undefined ? (
                    <p className="mt-3">Project type "{ selectedProjectType.description }" selected.</p>
                ) : null }
            </div>
            <hr />
            <div className="mb-3">
                <p>Enter your project address</p>
                <ProjectAddress formData={formData} setFormData={setFormData} />
            </div>
            <div>
                <ProjectDetails
                    label="Project Details"
                    disabled={selectedProjectType === undefined}
                    onChange={(projectDetails) => { setProjectDetails(projectDetails) }}
                />
            </div>
            <hr />
            <div className="mb-3">
                <p>Upload images for your project</p>
                <div className="row">
                    <ProjectImageUpload onFilesUploaded={(uploaded) => { setUploadedFiles(uploaded) }} />
                </div>
                <hr />
            </div>
            <div>
                <p>Choose materials for your project</p>
                <div className="row">
                    {materialsChoose}
                </div>
                <Button
                    color="success"
                    onClick={handleAddMaterialChoose}
                    className="btn btn-main mt-2"
                    disabled={selectedProjectType === undefined}
                >
                    Add another Material
                </Button>
            </div>
            <div>
                <div className="row">
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3 mt-auto text-end">
                        <Label>Total material budget</Label>
                    </div>
                    <div className="col-sm-3">
                        <Input type="number"
                            value={totalMaterialBudget}
                            className="text-end border border-success"
                            readOnly
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3 mt-auto text-end">
                        <Label>Total Usd</Label>
                    </div>
                </div>
                <div className="row mt-5">
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3 mt-auto text-end">
                        <Label>Total labor budget</Label>
                    </div>
                    <div className="col-sm-3">
                        <Input type="number"
                            value={totalLaborBudget}
                            className="text-end border"
                            onChange={handleTotalLaborBudgetChange}
                        />
                    </div>
                </div>
                <div className="row">
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3"></div>
                    <div className="col-sm-3 mt-auto text-end">
                        <Label>Total Usd</Label>
                    </div>
                </div>
                <div className="row my-3">
                    <div className="col-sm-10 d-table">
                        <span className={ !solution.error ? 'visually-hidden' : 'd-table-cell align-middle text-end pt-2 text-danger' }>
                            Oops, an error occurred creating your project. Please try again later.
                        </span>
                    </div>
                    <div className="col-sm-2 mt-auto text-end">
                        <Button
                            color="success"
                            onClick={submitProject}
                            className="btn btn-main mt-2"
                            disabled={ !validateProject() }
                        >
                        Create Project
                            <div className={ !solution.loading ? 'visually-hidden' : 'd-inline' }>
                                <Spinner className="ms-1" size="sm" />
                            </div>
                        </Button>
                    </div>
                </div>
            </div>
        </div>
        </Fragment>
    );
}

export default ProjectCreate;